import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Remembers the scroll position of an inner scroll container per history entry
 * and restores it when the user navigates Back to that entry.
 *
 * Our list pages (`/u/:username`, `/market`, `/inventory`) scroll an inner
 * `.main-content-scroll` div, not the window, so the browser's native scroll
 * restoration never applies — tapping a card and hitting Back dumps the user
 * at the top. This hook closes that gap.
 *
 * Keying on `location.key` means each history entry gets its own slot: a fresh
 * push has no stored value (starts at top), a POP finds one (restores).
 */

const PREFIX = 'cardsimp:scroll:';
const RESTORE_WINDOW_MS = 1200;

const read = (key: string): number | null => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: number) => {
  try {
    if (value > 0) sessionStorage.setItem(key, String(value));
    else sessionStorage.removeItem(key);
  } catch {
    // sessionStorage unavailable — silent no-op
  }
};

export function useScrollRestoration(
  el: HTMLElement | null | undefined,
  options: { enabled?: boolean; suffix?: string } = {},
): void {
  const { enabled = true, suffix = '' } = options;
  const location = useLocation();
  const storageKey = `${PREFIX}${location.key}:${location.pathname}${suffix ? `:${suffix}` : ''}`;
  const keyRef = useRef(storageKey);
  keyRef.current = storageKey;

  useEffect(() => {
    if (!enabled || !el) return;

    let rafId: number | null = null;
    let restoreRaf: number | null = null;

    const save = () => {
      rafId = null;
      write(keyRef.current, el.scrollTop);
    };

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(save);
    };

    // --- restore ---
    const target = read(storageKey);
    if (target !== null) {
      const startedAt = performance.now();
      const step = () => {
        restoreRaf = null;
        const maxTop = el.scrollHeight - el.clientHeight;
        if (maxTop >= target) {
          el.scrollTop = target;
          return; // reachable — done
        }
        // Content still hydrating (virtualized grid sizing, images loading):
        // park as close as possible and retry for a short window.
        el.scrollTop = maxTop;
        if (performance.now() - startedAt < RESTORE_WINDOW_MS) {
          restoreRaf = window.requestAnimationFrame(step);
        }
      };
      restoreRaf = window.requestAnimationFrame(step);
    }

    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (restoreRaf !== null) window.cancelAnimationFrame(restoreRaf);
      write(keyRef.current, el.scrollTop);
    };
    // Re-run when the element or the history entry changes.
  }, [el, enabled, storageKey]);
}
